import {
  Alert,
  Box,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiCustomDop } from '../../../api/hexa/ApiCustomDop';
import { HexaCustomDop } from '../../../api/hexa/models/CustomDopModels';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { filesize } from 'filesize';

interface CustomDopFilesProps {
  customDopId: string;
  fileName: string;
}

interface datePickerValue {
  date: Dayjs;
  stringDate: string;
}

const CustomDopFileList = ({ customDopId, fileName }: CustomDopFilesProps) => {
  const [rows, setRows] = useState<Partial<HexaCustomDop>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<datePickerValue>({
    date: dayjs(),
    stringDate: dayjs().format('YYYY-MM-DD'),
  });
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const { t } = useTranslation();

  /**
   * Open a new tab with the URL received from the API to download the file
   */
  const downloadFile = useCallback(async (urlDownload: string) => {
    setLoading(true);
    try {
      window.open(urlDownload, '_blank');
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} downloading custom dop file:
        ${error.response?.data.error ?? 'Error downloading custom dop file'}`,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Define the columns for the DataGrid
   */
  const columns = useMemo<GridColDef<Partial<HexaCustomDop>>[]>(
    () => [
      {
        field: 'file_name',
        headerName: 'File name',
        flex: 1,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'file_size',
        headerName: 'Size',
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          return params.row.file_size
            ? filesize(params.row.file_size, { round: 2 })
            : '';
        },
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={
              <Tooltip title={t('custom_dop_files.download')}>
                <DownloadForOfflineIcon color="primary" />
              </Tooltip>
            }
            onClick={() => downloadFile(params.row.url_download)}
            label={t('custom_dop_files.download')}
          />,
        ],
      },
    ],
    [downloadFile, t]
  );

  /**
   * Fetch custom dop files from the API and set the rows state
   */
  const fetchCustomDopFiles = useCallback(async () => {
    setLoading(true);
    try {
      if (fileName !== '') {
        const { total, files } = await ApiCustomDop.index_files(
          customDopId,
          fileName,
          selectedDate.stringDate
        );
        setRows(files);
        setRowCount(total);
      }
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching custom dop files:
        ${error.response?.data.error ?? 'Error fetching custom dop files'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customDopId, fileName, selectedDate]);

  useEffect(() => {
    fetchCustomDopFiles();
  }, [fetchCustomDopFiles]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  return (
    <>
      <Stack spacing={1}>
        <Typography variant={'h4'} sx={{ paddingY: '0.8rem' }}>
          {t('custom_dop_files.title')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '2rem',
            width: '100%',
          }}
        >
          <DataGridPremium
            sx={{ width: { xs: '100%', md: '60%' } }}
            getRowId={(row) => row.file_name ?? Math.random()}
            loading={loading}
            autoHeight={true}
            rows={rows}
            rowCount={rowCount}
            rowHeight={55}
            columns={columns}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              width: { xs: '100%', md: '30%' },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t('custom_dop_files.date_picker_label')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                label="Select a date"
                value={selectedDate.date}
                onChange={(value) => {
                  if (value) {
                    setSelectedDate({
                      date: value,
                      stringDate: value.format('YYYY-MM-DD'),
                    });
                  }
                }}
                shouldDisableDate={(date: Dayjs) => {
                  return date.isAfter(dayjs());
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Stack>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomDopFileList;
